import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
} from '@mui/material';
import { CompareDialogProps } from 'types/cx-editor';

// Mock clusters - will be replaced with API data in Phase 2
const MOCK_CLUSTERS_WYSIWYG = [
  { id: "cluster_001", name: "EE Treatment" },
  { id: "cluster_002", name: "AB Test - $ in Subject" },
  { id: "cluster_003", name: "AB Test - No $ in Subject" },
];

export const CompareDialog: React.FC<CompareDialogProps> = ({
  open,
  onOpenChange,
  variantA,
  variantB,
  onChange,
  onRun,
  availableTemplates,
  availableScenarios,
  availableClusters = MOCK_CLUSTERS_WYSIWYG,
}) => {
  const getScenariosForVariant = (templateKey: string) => {
    return availableScenarios.filter(s => {
      // In Phase 1, we use all available scenarios
      // In Phase 2, this will filter by templateKey via API
      return true;
    });
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleRun = () => {
    onRun();
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>Compare Template Variants</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Select two templates and their variants to compare them side-by-side.
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 3,
            mt: 1,
          }}
        >
          {[
            { variant: 'A' as const, data: variantA },
            { variant: 'B' as const, data: variantB }
          ].map(({ variant, data }) => {
            const scenarios = data ? getScenariosForVariant(data.templateKey) : [];
            return (
              <Box
                key={variant}
                sx={{
                  p: 3,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Variant {variant}
                  </Typography>
                  
                  <TextField
                    select
                    label="Template"
                    value={data?.templateKey || ''}
                    onChange={(e) => {
                      const newData = {
                        ...(data || { hierarchy: 'Project', locale: 'en_US', scenario: 'default' }),
                        templateKey: e.target.value,
                      };
                      onChange(variant, newData);
                    }}
                    fullWidth
                  >
                    {availableTemplates.map(t => (
                      <MenuItem key={t.templateKey} value={t.templateKey}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    label="Hierarchy"
                    value={data?.hierarchy || ''}
                    onChange={(e) => {
                      if (data) {
                        onChange(variant, { ...data, hierarchy: e.target.value });
                      }
                    }}
                    fullWidth
                    disabled={!data}
                  >
                    <MenuItem value="Project">Project</MenuItem>
                    {availableClusters.map(c => (
                      <MenuItem key={c.id} value={`Cluster: ${c.name}`}>
                        Cluster: {c.name}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    label="Locale"
                    value={data?.locale || 'en_US'}
                    onChange={(e) => {
                      if (data) {
                        onChange(variant, { ...data, locale: e.target.value });
                      }
                    }}
                    fullWidth
                    disabled={!data}
                  >
                    <MenuItem value="en_US">English (US)</MenuItem>
                    <MenuItem value="es_US">Spanish (US)</MenuItem>
                  </TextField>

                  <TextField
                    select
                    label="Scenario"
                    value={data?.scenario || 'default'}
                    onChange={(e) => {
                      if (data) {
                        onChange(variant, { ...data, scenario: e.target.value });
                      }
                    }}
                    fullWidth
                    disabled={!data || scenarios.length === 0}
                    helperText={scenarios.length === 0 ? "No scenarios available" : ""}
                  >
                    {scenarios.map(scenario => (
                      <MenuItem key={scenario.value} value={scenario.value}>
                        {scenario.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>Close</Button>
        <Button
          variant="contained"
          onClick={handleRun}
          disabled={!variantA || !variantB}
        >
          Compare
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompareDialog;
